import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BadgePercent, Check, ChevronLeft, Dumbbell, GraduationCap, HeartPulse, LayoutGrid, QrCode, RotateCcw, Search, Sparkles, Store, Users } from "lucide-react";
import "./ad.css";

const EASE = [0.22, 1, 0.36, 1];
const IMG = {
  rest: "https://images.unsplash.com/photo-1730965804534-eff996ac3249?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODH8MHwxfHNlYXJjaHwyfHx3YXJtJTIwaW5kaWFuJTIwY2FmZSUyMHJlc3RhdXJhbnQlMjBpbnRlcmlvcnxlbnwwfHx8MTc5MjA0OTMwOHww&ixlib=rb-4.1.0&q=85",
  salon: "https://images.unsplash.com/photo-1659355751282-5ca7807af9e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjBiYXJiZXJ8ZW58MHx8fHwxNzkyMDU0NjA1fDA&ixlib=rb-4.1.0&q=85",
  clinic: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA6OTV8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBkb2N0b3J8ZW58MHx8fHwxNzkyMDU0NjA1fDA&ixlib=rb-4.1.0&q=85",
  fitness: "https://images.unsplash.com/photo-1682241367368-6387d5d4921a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBneW0lMjBmaXRuZXNzJTIwdHJhaW5lciUyMHdvcmtvdXR8ZW58MHx8fDE3ODkyNzQwNzd8MA&ixlib=rb-4.1.0&q=85",
};
const CATS = ["Restaurants & Cafés", "Clinical Labs", "Salons", "Gyms", "Wellness", "Tuition", "Other Services"];
const CAT_KEY = { "Restaurants & Cafés": "restaurants", "Clinical Labs": "labs", "Salons": "salons" };
const FEEDS = {
  restaurants: [
    { name: "The Olive Table", cat: "Restaurants & Cafés", img: IMG.rest, by: "Rohan M.", init: "RM", text: "Great ambience, consistently good food, and a lovely place for dinner." },
    { name: "Spice Route", cat: "Restaurants & Cafés", img: IMG.rest, by: "Meera K.", init: "MK", text: "Their thali is the real deal. Book ahead on weekends." },
  ],
  labs: [
    { name: "City Diagnostics", cat: "Clinical Labs", img: IMG.clinic, by: "Priya S.", init: "PS", text: "Quick reports, clean facility, and home sample collection that actually shows up on time." },
    { name: "MedLabs Central", cat: "Clinical Labs", img: IMG.clinic, by: "Karan J.", init: "KJ", text: "Transparent pricing and reports on WhatsApp the same evening." },
  ],
  salons: [
    { name: "Glow Studio", cat: "Salons", img: IMG.salon, by: "Anaya R.", init: "AR", text: "Best haircut in the city. They actually listen to what you want." },
    { name: "The Barber House", cat: "Salons", img: IMG.salon, by: "Vikram T.", init: "VT", text: "Classic cuts, fair prices, zero waiting if you book ahead." },
  ],
};
const CONTACTS = [
  { name: "Aditi Shah", init: "AS", count: 3 },
  { name: "Rohan Mehta", init: "RM", count: 2 },
  { name: "Neha Kapoor", init: "NK", count: 4 },
];

const INITIAL = { scene: 1, cursor: { x: 50, y: 112, visible: false }, ripple: null, category: "restaurants", hoverCard: false, contactOpen: false, passState: "none", scanState: "none", payState: "none", montage: null, ctaClicked: false, ended: false, posted: false };
export const AD_DURATION = 43.2;

// Cursor moves resolve the REAL on-screen position of the target element at fire time.
function place(sel) {
  const stage = document.querySelector('[data-testid="ad-stage"]');
  const el = sel && document.querySelector(sel);
  if (!stage || !el) return { x: 50, y: 50 };
  const r = el.getBoundingClientRect();
  const b = stage.getBoundingClientRect();
  return { x: ((r.left + r.width / 2 - b.left) / b.width) * 100, y: ((r.top + r.height / 2 - b.top) / b.height) * 100 };
}
const moveTo = (t, sel) => [t, (prev) => ({ cursor: { ...place(sel), visible: true } })];
const clickOn = (t, sel, patch = {}) => [t, (prev) => { const p = place(sel); return { ...patch, cursor: { ...p, visible: true }, ripple: { id: t, ...p } }; }];
const fx = (t, fn) => [t, () => { fn(); return {}; }];
const scrollTabs = (left) => { const el = document.querySelector(".ad-tabs"); if (el) el.scrollTo({ left, behavior: "smooth" }); };
const centerTab = (sel) => { const el = document.querySelector(sel); if (el) el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" }); };

const SCRIPT = [
  [0, { scene: 1 }],
  [1.8, { scene: 11 }],
  moveTo(4.5, '[data-ad="post-btn"]'),
  clickOn(5.0, '[data-ad="post-btn"]', { posted: true }),
  [5.6, { scene: 2, posted: false }],
  moveTo(6.2, ".ad-tabs"),
  fx(6.2, () => scrollTabs(180)),
  fx(6.75, () => scrollTabs(0)),
  moveTo(6.9, '[data-ad="tab-restaurants"]'),
  clickOn(7.4, '[data-ad="tab-restaurants"]', { category: "restaurants" }),
  moveTo(8.2, '[data-ad="feed-card-0"]'),
  [8.35, { hoverCard: true }],
  clickOn(9.0, '[data-ad="feed-card-0"]', { scene: 3, hoverCard: false }),
  moveTo(11.5, '[data-ad="back"]'),
  clickOn(12.0, '[data-ad="back"]', { scene: 2 }),
  moveTo(12.6, '[data-ad="tab-contacts"]'),
  clickOn(13.1, '[data-ad="tab-contacts"]', { scene: 4 }),
  moveTo(14.2, '[data-ad="contact-0"]'),
  clickOn(14.7, '[data-ad="contact-0"]', { contactOpen: true }),
  moveTo(15.4, '[data-ad="mini-rec-0"]'),
  clickOn(15.9, '[data-ad="mini-rec-0"]', { scene: 5 }),
  moveTo(19.1, '[data-ad="use-rec"]'),
  clickOn(19.5, '[data-ad="use-rec"]', { passState: "selected" }),
  moveTo(20.3, '[data-ad="gen-pass"]'),
  clickOn(20.8, '[data-ad="gen-pass"]', { scene: 6, passState: "pass" }),
  moveTo(23.9, '[data-ad="view-qr"]'),
  clickOn(24.3, '[data-ad="view-qr"]', { scene: 7 }),
  [26.2, { scanState: "scanned" }],
  [27.1, { scanState: "verified" }],
  moveTo(28.1, '[data-ad="to-pay"]'),
  clickOn(28.6, '[data-ad="to-pay"]', { payState: "pay" }),
  [29.3, { payState: "discount" }],
  [29.9, { payState: "success" }],
  [30.6, { scene: 8 }],
  [34.6, { scene: 9, scanState: "none", payState: "none", passState: "none", contactOpen: false, category: "restaurants", montage: null }],
  moveTo(35.1, '[data-ad="tab-labs"]'),
  clickOn(35.6, '[data-ad="tab-labs"]', { category: "labs" }),
  fx(35.6, () => centerTab('[data-ad="tab-labs"]')),
  moveTo(36.3, '[data-ad="tab-salons"]'),
  clickOn(36.8, '[data-ad="tab-salons"]', { category: "salons" }),
  fx(36.8, () => centerTab('[data-ad="tab-salons"]')),
  [37.5, { montage: "gyms" }],
  [37.85, { montage: "wellness" }],
  [38.2, { montage: "tuition" }],
  [38.55, { montage: "other" }],
  [38.9, { scene: 10 }],
  moveTo(41.1, '[data-ad="cta"]'),
  clickOn(41.7, '[data-ad="cta"]', { ctaClicked: true }),
  [43.2, { ended: true }],
];

function LogoMark({ size = 30 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path d="M16 2C8.8 2 3 7.8 3 15c0 4 1.8 7.6 4.7 10L6 29l5.7-2.2c1.4.4 2.8.5 4.3.5 7.2 0 13-5.8 13-13S23.2 2 16 2Z" fill="#FFC53D" />
      <path d="M11.2 21.5V9.7h5.1c2.7 0 4.4 1.6 4.4 4 0 2-1.2 3.3-3 3.8l3.3 4h-3.1l-2.9-3.7h-1.6v3.7h-2.2Zm2.2-5.6h2.7c1.5 0 2.3-.8 2.3-2.1s-.8-2.1-2.3-2.1h-2.7v4.2Z" fill="#0A1428" />
      <circle cx="23.6" cy="8.4" r="2.6" fill="#0A1428" opacity="0.9" />
    </svg>
  );
}
function Avatar({ init, small }) { return <span className={`ad-avatar ${small ? "small" : ""}`}>{init}</span>; }
function TypedText({ text }) {
  const [n, setN] = useState(0);
  useEffect(() => { const id = setInterval(() => setN((v) => (v >= text.length ? v : v + 2)), 26); return () => clearInterval(id); }, [text]);
  return <>{text.slice(0, n)}</>;
}

function FeedCard({ item, hover, delay, idad }) {
  return (
    <motion.div className={`ad-feed-card ${hover ? "hover" : ""}`} data-ad={idad} initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay, ease: EASE }}>
      <img src={item.img} alt={item.name} />
      <div className="ad-feed-body">
        <strong>{item.name}</strong>
        <span className="ad-feed-cat">{item.cat}</span>
        <p>{item.text}</p>
        <div className="ad-feed-by"><Avatar init={item.init} small /> Recommended by {item.by}</div>
      </div>
    </motion.div>
  );
}

function Tabs({ category, source }) {
  return (
    <div className="ad-tabs-wrap">
      <div className="ad-tabs">
        {CATS.map((c) => <span key={c} data-ad={CAT_KEY[c] ? `tab-${CAT_KEY[c]}` : undefined} className={`ad-tab ${CAT_KEY[c] === category && source === "everyone" ? "active" : ""}`}>{c}</span>)}
      </div>
      <span data-ad="tab-contacts" className={`ad-tab ad-contacts-tab ${source === "contacts" ? "active" : ""}`}><Users size={11} /> My Contacts</span>
    </div>
  );
}

function HomeScreen({ s, feedKey }) {
  const feed = FEEDS[feedKey || s.category] || FEEDS.restaurants;
  return (
    <div className="ad-home">
      <motion.div className="ad-home-head" initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}>
        <span className="ad-home-brand"><LogoMark size={22} /> Recommora</span>
        <Avatar init="AR" />
      </motion.div>
      <motion.h3 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15, ease: EASE }}>Discover something <em>recommended</em> for you.</motion.h3>
      <motion.div className="ad-search" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}><Search size={14} /> Search salons, labs, gyms…</motion.div>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.5, ease: EASE }}><Tabs category={s.category} source="everyone" /></motion.div>
      <div className="ad-feed">
        <AnimatePresence mode="popLayout">
          {feed.map((item, i) => <FeedCard key={item.name + (feedKey || s.category)} item={item} hover={s.hoverCard && i === 0} delay={0.5 + i * 0.15} idad={i === 0 ? "feed-card-0" : undefined} />)}
        </AnimatePresence>
      </div>
    </div>
  );
}

function QRMatrix() {
  const cells = useMemo(() => {
    let seed = 99;
    const rand = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
    const g = [];
    const finder = (x, y) => (x < 7 && y < 7) || (x > 13 && y < 7) || (x < 7 && y > 13);
    const finderOn = (x, y) => { const lx = x > 13 ? x - 14 : x; const ly = y > 13 ? y - 14 : y; return lx === 0 || lx === 6 || ly === 0 || ly === 6 || (lx > 1 && lx < 5 && ly > 1 && ly < 5); };
    for (let y = 0; y < 21; y++) for (let x = 0; x < 21; x++) g.push(finder(x, y) ? finderOn(x, y) : rand() > 0.52);
    return g;
  }, []);
  return (
    <svg className="ad-qr" viewBox="0 0 21 21">
      {cells.map((on, i) => on ? <rect key={i} x={i % 21} y={Math.floor(i / 21)} width="1.02" height="1.02" className="ad-qr-px" style={{ animationDelay: `${((i % 21) + Math.floor(i / 21)) * 0.012}s` }} /> : null)}
    </svg>
  );
}

function PassCard({ mini }) {
  return (
    <div className={`ad-pass ${mini ? "mini" : ""}`}>
      <div className="ad-pass-top"><LogoMark size={mini ? 16 : 24} /><span>Recommora</span><span className="ad-pass-tag">QR PASS</span></div>
      <strong>The Olive Table</strong>
      <span className="ad-pass-by"><Avatar init="AS" small /> Recommended by Aditi Shah</span>
      <div className="ad-pass-qr"><QRMatrix /><div className="ad-scanline" /></div>
      {!mini && <small>Show this pass at the restaurant</small>}
    </div>
  );
}

function Scene({ children, k }) {
  return <motion.div className="ad-scene" key={k} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.18 } }} transition={{ duration: 0.32 }}>{children}</motion.div>;
}

export default function Advertisement() {
  const [s, setS] = useState(INITIAL);
  const [progress, setProgress] = useState(0);
  const timers = useRef([]);
  const raf = useRef(0);
  const startRef = useRef(0);

  const start = () => {
    timers.current.forEach(clearTimeout);
    cancelAnimationFrame(raf.current);
    setS(INITIAL);
    setProgress(0);
    startRef.current = performance.now();
    SCRIPT.forEach(([t, patch]) => timers.current.push(setTimeout(() => setS((prev) => ({ ...prev, ...(typeof patch === "function" ? patch(prev) : patch) })), t * 1000)));
    const loop = () => { const el = (performance.now() - startRef.current) / 1000; setProgress(Math.min(el / AD_DURATION, 1)); if (el < AD_DURATION + 0.8) raf.current = requestAnimationFrame(loop); };
    raf.current = requestAnimationFrame(loop);
  };
  useEffect(() => { start(); return () => { timers.current.forEach(clearTimeout); cancelAnimationFrame(raf.current); }; }, []);

  return (
    <div className="ad-page">
      <div className="ad-stage" data-testid="ad-stage">
        <div className="ad-glow" />
        <AnimatePresence mode="wait">

          {s.scene === 1 && <Scene k={1}>
            <div className="ad-center">
              <motion.div initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.7, ease: EASE }}><LogoMark size={88} /></motion.div>
              <motion.span className="ad-logo-word" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.55, ease: EASE }}>Recommora</motion.span>
              <motion.span className="ad-logo-sub" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.75 }}>Trust the people you know.</motion.span>
            </div>
          </Scene>}

          {s.scene === 11 && <Scene k={11}>
            <div className="ad-center">
              <motion.div className="ad-post-card" initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}>
                <span className="ad-source-chip">NEW RECOMMENDATION</span>
                <div className="ad-post-head"><Avatar init="AS" /><div><strong>Aditi Shah</strong><small>is recommending a place</small></div></div>
                <div className="ad-post-biz"><img src={IMG.rest} alt="" /><div><strong>The Olive Table</strong><small>Restaurants & Cafés</small></div></div>
                <p className="ad-post-text"><TypedText text="The food is consistently good, the ambience is lovely — perfect for a relaxed dinner with friends." /></p>
                {!s.posted
                  ? <span className="ad-btn solid big" data-ad="post-btn">Post recommendation</span>
                  : <motion.div className="ad-toast" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.35, ease: EASE }}><Check size={13} /> Posted to Aditi’s circle.</motion.div>}
              </motion.div>
            </div>
          </Scene>}

          {s.scene === 2 && <Scene k={2}><HomeScreen s={s} /></Scene>}

          {s.scene === 3 && <Scene k={3}>
            <div className="ad-sheet">
              <div className="ad-sheet-bar" data-ad="back"><ChevronLeft size={16} /> <span>Everyone on Recommora</span></div>
              <motion.div className="ad-detail" initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
                <img src={IMG.rest} alt="The Olive Table" />
                <div className="ad-detail-body">
                  <strong>The Olive Table</strong>
                  <div className="ad-feed-by"><Avatar init="RM" small /> Recommended by Rohan M.</div>
                  <p>“Great ambience, consistently good food, and a lovely place for dinner.”</p>
                  <span className="ad-source-chip">From the wider Recommora community</span>
                </div>
              </motion.div>
            </div>
          </Scene>}

          {s.scene === 4 && <Scene k={4}>
            <div className="ad-sheet">
              <Tabs category={s.category} source="contacts" />
              <h3 className="ad-contacts-title">Recommended by <em>people you know.</em></h3>
              <div className="ad-contacts">
                {CONTACTS.map((c, i) => (
                  <motion.div key={c.name} data-ad={`contact-${i}`} className={`ad-contact ${s.contactOpen && i === 0 ? "open" : ""}`} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.12, duration: 0.45, ease: EASE }}>
                    <div className="ad-contact-row"><Avatar init={c.init} /><div><strong>{c.name}</strong><small>{c.count} recommendations</small></div></div>
                    {s.contactOpen && i === 0 && (
                      <motion.div className="ad-contact-recs" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} transition={{ duration: 0.4, ease: EASE }}>
                        <span className="ad-link-line" />
                        <div className="ad-mini-rec" data-ad="mini-rec-0"><img src={IMG.rest} alt="" /><div><strong>The Olive Table</strong><small>Restaurants & Cafés</small></div></div>
                        <div className="ad-mini-rec dim"><img src={IMG.salon} alt="" /><div><strong>Glow Studio</strong><small>Salons</small></div></div>
                      </motion.div>
                    )}
                  </motion.div>
                ))}
              </div>
              <span className="ad-sim-note">Simulated contacts — consent-based, demonstration only</span>
            </div>
          </Scene>}

          {s.scene === 5 && <Scene k={5}>
            <div className="ad-sheet">
              <motion.div className="ad-biz" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}>
                <img className="ad-biz-img" src={IMG.rest} alt="The Olive Table" />
                <div className="ad-biz-body">
                  <strong>The Olive Table</strong>
                  <div className="ad-biz-by"><span className="ad-link-line" /><Avatar init="AS" small /> Recommended by Aditi Shah</div>
                  <motion.p className="ad-biz-quote" animate={{ scale: [1, 1.03, 1] }} transition={{ duration: 2.2, delay: 0.5, ease: EASE }}>“This is one of my favourite places for a relaxed dinner. The food is consistently good, the ambience is lovely, and it’s a great place to catch up with friends.”</motion.p>
                  {s.passState === "none" && <div className="ad-biz-actions"><span className="ad-btn ghost">View Business</span><span className="ad-btn solid" data-ad="use-rec">Use Aditi’s Recommendation</span></div>}
                  {s.passState === "selected" && <>
                    <motion.div className="ad-toast" initial={{ opacity: 0, y: 12, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.35, ease: EASE }}><Check size={13} /> Recommendation selected.</motion.div>
                    <motion.span className="ad-btn solid big" data-ad="gen-pass" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.35, ease: EASE }}><QrCode size={15} /> Generate My QR Pass</motion.span>
                  </>}
                </div>
              </motion.div>
            </div>
          </Scene>}

          {s.scene === 6 && <Scene k={6}>
            <div className="ad-center">
              <motion.div initial={{ opacity: 0, y: 90, rotate: -8, scale: 0.85 }} animate={{ opacity: 1, y: 0, rotate: 0, scale: 1 }} transition={{ duration: 0.8, ease: EASE }}><PassCard /></motion.div>
              <motion.span className="ad-btn solid big" data-ad="view-qr" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.7, duration: 0.35, ease: EASE }}>View QR Pass</motion.span>
              <motion.p className="ad-pass-msg" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}>Your recommendation. <em>Ready to redeem.</em></motion.p>
            </div>
          </Scene>}

          {s.scene === 7 && <Scene k={7}>
            {s.payState === "none" && <div className="ad-checkout">
              <div className="ad-counter-label">The Olive Table · Checkout</div>
              <div className="ad-counter">
                <motion.div className="ad-terminal" initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, ease: EASE }}>
                  <div className="ad-terminal-screen">{s.scanState === "none" ? "SCAN PASS" : s.scanState === "scanned" ? "READING…" : "VERIFIED ✓"}</div>
                  <div className="ad-terminal-pad">{Array.from({ length: 9 }).map((_, i) => <span key={i} />)}</div>
                </motion.div>
                <motion.div className="ad-phone" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, ease: EASE }}>
                  <PassCard mini />
                  <div className="ad-beam" />
                </motion.div>
              </div>
              <div className="ad-statuses">
                {s.scanState !== "none" && <motion.span className="ad-status" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}><Check size={13} /> QR Pass Scanned</motion.span>}
                {s.scanState === "verified" && <motion.span className="ad-status gold" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}><Check size={13} /> Recommendation Verified</motion.span>}
                {s.scanState === "verified" && <motion.span className="ad-btn solid big" data-ad="to-pay" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}>Continue to Payment</motion.span>}
              </div>
            </div>}
            {s.payState !== "none" && <div className="ad-center">
              <motion.div className="ad-pay" initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}>
                <span className="ad-pay-brand"><LogoMark size={18} /> Recommora Pay <em>simulated</em></span>
                <strong>The Olive Table</strong>
                <div className="ad-pay-row"><span>Bill amount</span><b>₹1,200</b></div>
                <AnimatePresence>{s.payState !== "pay" && <motion.div className="ad-pay-row disc" initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }}><span><BadgePercent size={13} /> Customer discount 5%</span><b>− ₹60</b></motion.div>}</AnimatePresence>
                <div className="ad-pay-row total"><span>Total</span><b>{s.payState === "pay" ? "₹1,200" : "₹1,140"}</b></div>
                {s.payState === "success" && <motion.div className="ad-pay-success" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4, ease: EASE }}><span className="ad-pay-check"><Check size={18} /></span> Payment Successful</motion.div>}
                {s.payState === "success" && <motion.span className="ad-status gold" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>5% Customer Discount Applied</motion.span>}
              </motion.div>
            </div>}
          </Scene>}

          {s.scene === 8 && <Scene k={8}>
            <div className="ad-center ad-rewards">
              <svg className="ad-reward-lines" viewBox="0 0 100 40" preserveAspectRatio="none">
                <motion.path d="M50 8 L20 32" stroke="#FFC53D" strokeWidth="0.7" fill="none" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 1.6, duration: 0.5 }} />
                <motion.path d="M50 8 L80 32" stroke="#FFC53D" strokeWidth="0.7" fill="none" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 1.8, duration: 0.5 }} />
              </svg>
              <motion.div className="ad-reward-card" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.5, ease: EASE }}>
                <BadgePercent size={18} /><b>5% OFF</b><span>The Customer</span><p>Discover places you trust and save.</p><motion.small className="ad-save" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>Saved ₹60 tonight</motion.small>
              </motion.div>
              <motion.div className="ad-reward-card" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.5, ease: EASE }}>
                <Sparkles size={18} /><b>5% REWARDS</b><span>The Recommender</span><p>Good recommendations deserve appreciation.</p><motion.small className="ad-save" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}>Aditi’s rewards ↑</motion.small>
              </motion.div>
              <motion.div className="ad-reward-card" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.5, ease: EASE }}>
                <Store size={18} /><b>MORE CUSTOMERS</b><span>The Business</span><p>Turn trusted recommendations into new visits.</p><motion.small className="ad-save" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.9 }}>New visit confirmed</motion.small>
              </motion.div>
            </div>
          </Scene>}

          {s.scene === 9 && <Scene k={9}>
            <HomeScreen s={s} />
            <AnimatePresence>
              {s.montage && <motion.div className="ad-montage" key={s.montage} initial={{ opacity: 0, y: 30, scale: 0.92 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -18, scale: 0.95 }} transition={{ duration: 0.24, ease: EASE }}>
                {s.montage === "gyms" && <><Dumbbell size={16} /> Gyms & Fitness</>}
                {s.montage === "wellness" && <><HeartPulse size={16} /> Wellness</>}
                {s.montage === "tuition" && <><GraduationCap size={16} /> Tuition & Education</>}
                {s.montage === "other" && <><LayoutGrid size={16} /> Other Services</>}
              </motion.div>}
            </AnimatePresence>
          </Scene>}

          {s.scene === 10 && <Scene k={10}>
            <div className="ad-finale">
              <div className="ad-finale-dots">{Array.from({ length: 10 }).map((_, i) => <motion.span key={i} initial={{ x: Math.cos(i * 0.63) * 130, y: Math.sin(i * 0.63) * 200, opacity: 0 }} animate={{ x: 0, y: 0, opacity: [0, 1, 0] }} transition={{ duration: 1.3, delay: 0.1 + i * 0.04, ease: EASE }} />)}</div>
              <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.55, duration: 0.6, ease: EASE }}><LogoMark size={64} /></motion.div>
              <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.6, ease: EASE }}>Good recommendations<br /><em>deserve rewards.</em></motion.h1>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}>Discover. Recommend. Get rewarded.</motion.p>
              {!s.ctaClicked
                ? <motion.span className="ad-btn solid big" data-ad="cta" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6, duration: 0.45, ease: EASE }}>Join the Recommora Early-Access List</motion.span>
                : <motion.span className="ad-joined" initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.45, ease: EASE }}><Check size={18} /> Be part of what’s next.</motion.span>}
            </div>
          </Scene>}

        </AnimatePresence>

        <div className={`ad-cursor ${s.cursor.visible ? "on" : ""}`} style={{ left: `${s.cursor.x}%`, top: `${s.cursor.y}%` }}>
          <svg viewBox="0 0 24 24"><path d="M4 2l16 11-7.2 1.4L9.4 22z" fill="#FFC53D" stroke="#0A1428" strokeWidth="1.4" /></svg>
        </div>
        {s.ripple && <span key={s.ripple.id} className="ad-ripple" style={{ left: `${s.ripple.x}%`, top: `${s.ripple.y}%` }} />}

        <div className="ad-progress"><div style={{ width: `${progress * 100}%` }} /></div>
        <button className={`ad-replay ${s.ended ? "show" : ""}`} onClick={start} data-testid="ad-replay-button"><RotateCcw size={18} /></button>
      </div>
    </div>
  );
}
